<template>
  <div class="filter-manager p-3">
    <validation-observer ref="formValidator">
      <div class="row">
        <div class="col">
          <div class="row">
            <div
              :class="`${filter.class || 'col'} col-sm my-1`"
              v-for="filter in mainFilters"
              :key="filter.name"
            >
              <validation-provider
                :rules="filter.validations"
                v-slot="{ errors }"
              >
                <component
                  v-model="value[filter.name]"
                  :is="filter.component"
                  :class-container="filter.classContainer"
                  :fieldname="filter.fieldName"
                  :fieldkey="filter.fieldKey"
                  :options="filter.options"
                  :placeholder="filter.placeholder"
                  :mask-type="filter.maskType"
                  :masked="filter.masked != undefined ? filter.masked : false"
                  :error="filter.error && errors[0] ? filter.error : errors[0]"
                  :custom-error-msg="
                    filter.error && errors[0] ? filter.error : errors[0]
                  "
                  :type="filter.type"
                  :i="filter.icon"
                  :icon="filter.icon"
                  :left-icon="filter.leftIcon"
                  @input="changeFilter(filter)"
                  @change="changeFilter(filter)"
                  @keypress="onEnter(filter)"
                  :label="filter.label"
                  :clearable="filter.clearable"
                  :disabled="disabled(filter)"
                  :variant="filter.variant"
                  :is-inline="filter.isInline"
                  :max-date="filter.maxDate"
                  :min-date="filter.minDate"
                  :format="filter.format"
                  :use-utc="filter.useUtc"
                  v-bind="filter.props"
                  :input-name="filter.inputName"
                />
              </validation-provider>
            </div>
            <slot />
          </div>
        </div>
        <div
          v-if="searchText"
          :class="
            `${searchContainerClass} col-sm-12 col-md-12 col-lg-3 ml-0 mr-0 my-1`
          "
        >
          <div
            class="d-flex justify-content-md-end justify-content-sm-start no-actionbtn-width"
          >
            <div
              class="btn btn-sm toggle-btn mr-2"
              v-if="showAdvancedOptions"
              :class="{ 'advanced-button': advanced }"
              @click="advancedOptionsClick"
            >
              <i class="fal fa-sliders-v mr-2 ml-2 mt-2" />
            </div>
            <action-button
              :disabled="disabledSearch"
              @click="search"
              :i="searchIcon"
              class="col-sm filter-search-button"
              :text="searchText"
            />
          </div>
        </div>
      </div>

      <div v-if="advanced">
        <div class="row py-3">
          <div class="col-md-12">
            <span
              class="separator d-flex align-items-center color-gray-8 font-14"
            >
              FILTROS
            </span>
          </div>
        </div>
      </div>

      <div v-if="advanced" class="row">
        <div v-if="$slots.header_advanced" class="col-12">
          <slot name="header_advanced" />
        </div>
        <div
          :class="`${filter.class || 'col'} col-sm my-1`"
          v-for="filter in advancedFilters"
          :key="filter.name"
        >
          <validation-provider :rules="filter.validations" v-slot="{ errors }">
            <component
              v-model="value[filter.name]"
              :is="filter.component"
              :class-container="filter.classContainer"
              :name="
                filter.componentName != undefined ? filter.componentName : ''
              "
              :fieldname="filter.fieldName"
              :fieldkey="filter.fieldKey"
              :options="filter.options"
              :placeholder="filter.placeholder"
              :mask-type="filter.maskType"
              :masked="filter.masked != undefined ? filter.masked : false"
              :error="filter.error && errors[0] ? filter.error : errors[0]"
              :custom-error-msg="
                filter.error && errors[0] ? filter.error : errors[0]
              "
              :type="filter.type"
              :i="filter.icon"
              :icon="filter.icon"
              :left-icon="filter.leftIcon"
              @input="changeFilter(filter)"
              @change="changeFilter(filter)"
              @keypress="onEnter(filter)"
              :label="filter.label"
              :clearable="filter.clearable"
              :disabled="disabled(filter)"
              :variant="filter.variant"
              :is-inline="filter.isInline"
              :max-date="filter.maxDate"
              :min-date="filter.minDate"
              :format="filter.format"
              :use-utc="filter.useUtc"
              v-bind="filter.props"
              :input-name="filter.inputName"
            />
          </validation-provider>
        </div>
        <slot name="advanced" />
      </div>
    </validation-observer>
  </div>
</template>

<script>
import { ValidationObserver, ValidationProvider } from 'vee-validate';

// TODO: Cambiar ruta cuando los componentes se migren a shared
import CustomInput from 'CustomInput';
import CustomDropDown from 'custom-drop-down';
import ActionButton from 'ActionButton';
import CustomRadioGroup from 'custom-radio';
import CustomDatePicker from 'custom-date-picker';
import CustomToggle from '@quisit/mipe-custom-toggle';
import CustomMultiSelect from '@quisit/multiselect';

export default {
  name: 'FilterManager',
  components: {
    ValidationObserver,
    ValidationProvider,
    CustomInput,
    CustomDropDown,
    ActionButton,
    CustomRadioGroup,
    CustomDatePicker,
    CustomToggle,
    CustomMultiSelect,
  },
  props: {
    filters: {
      type: Array,
      default: () => [],
    },
    displayAdvanced: {
      type: Boolean,
      default: () => true,
    },
    openAdvanced: {
      type: Boolean,
      default: () => false,
    },
    disabledSearch: {
      type: Boolean,
      default: () => false,
    },
    searchText: {
      type: String,
      default: () => 'Buscar',
    },
    searchIcon: {
      type: String,
      default: () => 'fa fa-search',
    },
    searchClass: {
      type: String,
      default: () => '',
    },
    searchContainerClass: {
      type: String,
      default: () => '',
    },
    value: {
      type: Object,
      default: () => ({}),
    },
  },
  data() {
    return {
      advanced: false,
      showAdvancedOptions: this.displayAdvanced,
    };
  },
  computed: {
    filtersVisible() {
      return this.filters.filter(
        (filter) => filter.visible || filter.visible == null
      );
    },
    advancedFilters() {
      return this.filtersVisible.filter((filter) => filter.isAdvanced);
    },
    mainFilters() {
      return this.filtersVisible.filter(
        (filter) => !filter.isAdvanced || filter.isAdvanced == null
      );
    },
  },
  watch: {
    openAdvanced: {
      handler(newValue) {
        this.advanced = newValue;
      },
      immediate: true,
    },
  },
  mounted() {
    //Execute methods immediate
    this.filtersVisible.forEach((filter) => {
      if (filter.immediateOnChanged) {
        filter.onChanged(this.value[filter.name]);
      }
    });

    this.filters.forEach((filter) => {
      if (filter.clearModelsOnUpdate) {
        // is array
        this.$watch(`value.${filter.name}`, () => {
          filter.clearModelsOnUpdate.forEach((other) => {
            if (typeof other == 'string') {
              this.value[other] = null;
            } else if (typeof other == 'object') {
              this.value[other.name] = other.default;
            }
          });
        });
      }
    });
  },
  created() {
    //initialize every filter value
    this.filters.forEach((filter) => {
      this.$set(
        this.value,
        filter.name,
        this.value[filter.name] || filter.initialValue
      );
    });
  },
  methods: {
    advancedOptionsClick() {
      this.advanced = !this.advanced;
      this.$emit('update:openAdvanced', this.advanced);
    },
    async search(props = {}) {
      const isValid = await this.$refs.formValidator.validate();
      this.$emit('search', isValid ? { ...this.value, ...props } : null);
    },
    assignValue(key, value) {
      this.value[key] = value;
    },
    changeFilter(filter) {
      let data = filter.returnModel ? this.value : this.value[filter.name];

      if (filter.onChanged) {
        filter.onChanged(data);
      } else {
        this.$emit(filter.name, data);
      }
    },
    onEnter(filter) {
      if (
        (filter.component.toLowerCase() == 'custominput' &&
          filter.searchable == undefined) ||
        filter.searchable
      ) {
        this.search();
      }
    },
    disabled(filter) {
      if (filter.disabled) {
        if (typeof filter.disabled == 'boolean') {
          return filter.disabled;
        }

        let isDisabled = filter.disabled.some((other) => {
          let filterConfig = this.filtersVisible.find((x) => x.name == other);
          return this.value[other] == null && filterConfig;
        });

        return isDisabled;
      }
      return false;
    },
  },
};
</script>
<style lang="scss" scoped>
::v-deep input[type='checkbox']:checked + label span {
  background-color: #2db782 !important;
}
</style>
